import React from 'react';
import useServiceHook from "@hooks/useServiceHook";
import SubscriptionService, {
    processCancellationQueryParams,
    ProcessCancellationResponse
} from "@services/subscription/SubscriptionService";
import { useMsal } from '@azure/msal-react';
import { SelectedSubscriptionState } from './selectedSubscriptionReducer';
import { CancellationReasonState } from './cancellationReasonReducer';
import { CancellationVehicleState } from './cancellationVehicleReducer';
import { CancellationChargesState } from './cancellationChargesReducer';
import { Country } from '@utils/constants/localisation';
import Circular from "@components/spinners/Circular/Circular";
import {NavBar} from "@components/navigation/NavBar/NavBar";
import {ConfirmationBox, WarningBox, ErrorBox} from "@components/cards/messageBox";
import Button from "@components/buttons/Button/Button";
import ExternalLink from "@assets/images/external_link.svg";

type Props = {
    selectedSubscriptionState: SelectedSubscriptionState;
    cancellationReasonState: CancellationReasonState;
    cancellationVehicleState: CancellationVehicleState;
    cancellationChargesState: CancellationChargesState;
    country: Country;
};


export default function CancellationResult({
    selectedSubscriptionState,
    cancellationReasonState,
    cancellationVehicleState,
    cancellationChargesState,
    country
}: Props) {
    const msalContext = useMsal();
    const userDetails = msalContext.instance.getActiveAccount();

    // Build the cancellation request from reducer states
    const cancellationRequest: processCancellationQueryParams = {
        country: country,
        cancellation_date: cancellationVehicleState.cancellationDate,
        charges: [
            ...(cancellationChargesState.calculated_charges),
            ...(cancellationChargesState.ancillary_charges)
        ],
        fleetio_vehicle_id: Number(cancellationVehicleState.vehicleId),
        vehicle_odometer: Number(cancellationVehicleState.endingKms),
        vehicle_return_status_id: Number(cancellationVehicleState.fleetioStatusId),
        cancellation_type: cancellationReasonState.cancellationType,
        cancellation_reason_outcome: cancellationReasonState.cancellationCategory,
        cancellation_reason_reason: cancellationReasonState.cancellationSubCategory,
        cancellation_additional_info: cancellationReasonState.cancellationAdditionalInfo,

        customer_returning_date: cancellationReasonState.customerReturnDate,
        add_restriction_to_account: (cancellationReasonState.cancellationAddRestrictionToAccount).toString().toLowerCase() === 'yes',

        vehicle_remarketable: cancellationReasonState.vehicleRemarketable,

        csr_email: userDetails?.username || 'Unknown',
    };

    // Call the BE to process cancellation
    const [cancellationProcessing, , cancellationErrMsg, cancellationResult] = useServiceHook<ProcessCancellationResponse>(
        SubscriptionService.putProcessCancellation_v2,
        [cancellationRequest, selectedSubscriptionState.selectedSubsId],
        true,
        [selectedSubscriptionState.selectedSubsId]
    );


    return (
        <>
            <NavBar pageTitle="Complete cancellation in Chargebee"/>
            <div style={{marginTop: '1.5vh'}}/>

            {cancellationProcessing ? (
                <div style={{ textAlign: 'center', marginTop: '2vh' }}>
                    <Circular testId="cancellation-loading-spinner" />
                </div>
            ) : cancellationResult ? (
                <>
                    <WarningBox message={"Please proceed to Chargebee to cancel the subscription. " +
                        "Verify that the correct charges and addons have been applied, apply credits as needed, and " +
                        "issue the invoice."}/>

                    {/* Chargebee result: show error box ONLY if automated charge application failed */}
                    {cancellationResult.chargebee_result.success ? (
                        <>
                            <div style={{marginTop: '1.5vh'}}/>
                            <ConfirmationBox
                                message={
                                    <div>
                                        Charges were successfully applied to the Chargebee Subscription.
                                    </div>
                                }
                            />
                        </>
                    ) :  (
                        <>
                            <div style={{marginTop: '1.5vh'}}/>
                            <ErrorBox
                                message={
                                    <div>
                                        <div>An error was encountered applying the charges to the Chargebee
                                            Subscription.
                                        </div>
                                    </div>
                                }
                            />
                        </>
                    )}

                    {/*Fleetio result*/}
                    <div style={{marginTop: '1.5vh'}}/>
                    {cancellationResult.fleetio_result.success ? (
                        <>
                            <ConfirmationBox message={"Vehicle unassigned in Fleetio"}/>
                            <p>
                                The vehicle
                                 {cancellationResult.fleetio_result?.fleetio_vehicle_description || 'Unknown vehicle'}
                                 has been unassigned from the subscription.
                                 {cancellationResult.fleetio_result?.fleetio_vehicle_url && (
                                    <> {' '}
                                        <a
                                            href={cancellationResult.fleetio_result.fleetio_vehicle_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View in Fleetio
                                        </a>
                                    </>
                                )}
                            </p>
                        </>
                    ) : (
                        <>
                            <ErrorBox message={"Failed to unassign vehicle in Fleetio"}/>
                            <p>
                                Error occurred
                                for: <b>{cancellationResult.fleetio_result.fleetio_vehicle_description || 'Unknown vehicle'}.</b>
                                {cancellationResult.fleetio_result.fleetio_vehicle_url && (
                                    <> {' '}
                                        <a
                                            href={cancellationResult.fleetio_result.fleetio_vehicle_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View in Fleetio
                                        </a>
                                    </>
                                )}
                            </p>
                        </>
                    )}

                    {/*Zoho Subscription result*/}
                    <div style={{marginTop: '1.5vh'}}/>
                    {cancellationResult.zoho_subscription_result.success ? (
                        <>
                            <ConfirmationBox message={"Zoho customer subscription status has been set to inactive."}/>
                            <p>
                                <> {' '}
                                    <a
                                        href={cancellationResult.zoho_subscription_result.zoho_subscription_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View subscription in Zoho
                                    </a>
                                </>
                            </p>
                        </>
                    ) : (
                        <>
                            <ErrorBox message={"Failed to update Zoho subscription status"}/>
                            <p>
                                <a
                                    href={cancellationResult.zoho_subscription_result.zoho_subscription_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View subscription in Zoho
                                </a>
                            </p>
                        </>
                    )}

                    {/*Zoho Cancellation*/}
                    <div style={{marginTop: '1.5vh'}}/>
                    {cancellationResult.zoho_cancellation_result.success ? (
                        <>
                            <ConfirmationBox message={"Zoho Cancellation Record Created."}/>
                            <p>
                                {cancellationResult.zoho_cancellation_result.zoho_cancellation_url && cancellationResult.zoho_cancellation_result.zoho_cancellation_url !== '' ? (
                                <> {' '}
                                    <a
                                        href={cancellationResult.zoho_cancellation_result.zoho_cancellation_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View cancellation record in Zoho
                                    </a>
                                </>
                            ) : 'No Zoho cancellation link available.'}
                            </p>
                        </>
                    ) : (
                        <>
                            <ErrorBox message={"Failed to create Zoho cancellation record"}/>
                            <p>
                                {cancellationResult.zoho_cancellation_result.zoho_cancellation_url && cancellationResult.zoho_cancellation_result.zoho_cancellation_url !== '' ? (
                                    <a
                                        href={cancellationResult.zoho_cancellation_result.zoho_cancellation_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View cancellation record in Zoho
                                    </a>
                                ) : 'No Zoho cancellation link available.'}
                            </p>
                        </>
                    )}

                    <div style={{marginTop: '3vh'}}/>
                    <Button
                        label="View in Chargebee"
                        iconSrc={ExternalLink}
                        iconRight={true}
                        onClickFunc={() => {
                            console.debug('Opening Chargebee subscription URL:', cancellationResult.chargebee_result?.chargebee_subscription_url);
                            window.open(cancellationResult.chargebee_result?.chargebee_subscription_url || '', '_blank', 'noopener,noreferrer');
                        }}
                    />

                </>
            ) : (
                //TODO is the endpoint going to return error messages (to display here)
                <WarningBox message={cancellationErrMsg || "Something went wrong"}/>
            )
            }
        </>
    );
};
